import { styled, type Style } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { MouseEventHandler } from 'react'
import { LinkAnchor } from '../link/LinkAnchor'

export type ButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  label?: string
  icon?: IconDefinition
  iconPosition?: 'left' | 'right'
  disabled?: boolean
  to?: string
  type?: 'button' | 'submit' | 'reset'
  style?: Style
  onClick?: MouseEventHandler<HTMLElement>
  onMouseDown?: MouseEventHandler<HTMLElement>
}

export type ButtonSize = 'small' | 'medium'
export type ButtonVariant = 'primary' | 'secondary'

const sizeStyle: Record<ButtonSize, Style> = {
  medium: {
    height: '3rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    gap: '0.5rem',
    fontSize: '1.125rem',
  },
  small: {
    height: '2rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    gap: '0.25rem',
    fontSize: '1rem',
  },
}
const sizeIconStyle: Record<ButtonSize, Style> = {
  medium: {
    height: '1.25rem',
    width: '1.25rem',
  },
  small: {
    height: '1rem',
    width: '1rem',
  },
}
const variantStyle: Record<ButtonVariant, Style> = {
  primary: {
    color: 'var(--palette-white)',
    background: 'var(--brand-primary)',
    border: '2px solid var(--brand-primary)',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    '&:hover': {
      background: 'var(--palette-red-600)',
      border: '2px solid var(--palette-red-600)',
    },
    '&:active': {
      background: 'var(--palette-red-600)',
      border: '2px solid var(--palette-red-600)',
    },
    '&:disabled': {
      background: 'var(--palette-red-200)',
      border: '2px solid var(--palette-red-200)',
    },
  },
  secondary: {
    color: 'var(--brand-secondary)',
    background: 'transparent',
    border: '2px solid var(--brand-secondary)',
    paddingTop: '0.8125rem',
    paddingBottom: '0.8125rem',
    '&:hover': {
      background: 'var(--brand-secondary)',
      color: 'var(--palette-white)',
    },
    '&:active': {
      background: 'var(--brand-secondary)',
      color: 'var(--palette-white)',
    },
    '&:disabled': {
      border: '2px solid var(--palette-blue-200)',
      color: 'var(--palette-blue-200)',
    },
    '&:hover:disabled': {
      background: 'transparent',
      cursor: 'not-allowed',
    },
  },
}

export const Button = ({
  variant = 'primary',
  size = 'medium',
  label = undefined,
  icon,
  iconPosition = 'right',
  disabled = false,
  to,
  type = 'button',
  style,
  onClick,
  onMouseDown,
}: ButtonProps) => {
  const faIcon = icon ? renderIcon(icon, variant, size) : undefined

  const content = renderContent(label, faIcon, iconPosition)
  const buttonStyle = {
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '1.875rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    '&:hover:disabled': {
      cursor: 'not-allowed',
    },
    ...variantStyle[variant],
    ...sizeStyle[size],
    ...style,
  } satisfies Style

  if (to) {
    return (
      <LinkAnchor
        disabled={disabled}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement> | undefined}
        onMouseDown={
          onMouseDown as MouseEventHandler<HTMLAnchorElement> | undefined
        }
        style={buttonStyle}
        to={to}
      >
        {content}
      </LinkAnchor>
    )
  }

  return (
    <styled.button
      disabled={disabled}
      onClick={onClick as MouseEventHandler<HTMLButtonElement> | undefined}
      onMouseDown={
        onMouseDown as MouseEventHandler<HTMLButtonElement> | undefined
      }
      type={type}
      style={buttonStyle}
    >
      {content}
    </styled.button>
  )
}

const renderIcon = (
  icon: IconDefinition,
  variant: ButtonVariant,
  size: ButtonSize,
) => {
  return (
    <FontAwesomeIcon
      key="icon"
      icon={icon}
      style={{ ...sizeIconStyle[size] }}
    />
  )
}

const renderContent = (
  text?: JSX.Element | string,
  icon?: JSX.Element,
  iconPosition?: 'left' | 'right',
) => {
  const hasText = text !== undefined && text !== ''
  const hasIcon = icon !== undefined

  if (!hasText && !hasIcon) {
    throw Error('The button has neither text nor icon')
  }

  if (!hasText || !hasIcon) {
    return hasText ? text : icon
  }
  return iconPosition === 'right' ? [text, icon] : [icon, text]
}
